import {useMemo} from 'react';
import {Control, RegisterOptions, useController} from 'react-hook-form';
import {TagsInput as TagsInputComponent} from '@littlewall/react-tag-input-component';
import clsx from 'clsx';
import toastify from '../../../../helpers/toast/toastify';
import * as inputStyles from './input.module.css';
import * as styles from './tagsInput.module.css';

interface ITagsInput {
    caption?: string,
    control: Control,
    name: string,
    placeholder?: string,
    hasUniqueValues?: boolean,
    title?: string,
    icon?: JSX.Element,
    rules?: RegisterOptions,
}

const TagsInput = ({
    caption,
    control,
    name,
    placeholder,
    title,
    icon,
    rules,
    hasUniqueValues = true,
}: ITagsInput) => {
    const {
        field: {
            onChange,
            value,
        },
        fieldState: {
            error,
        },
    } = useController({
        name,
        control,
        rules,
    });
    const isError = !!(error);
    const values = useMemo(() => {
        if (value === '') {
            return [];
        }

        const tagsArray = value?.split(',');

        if (tagsArray && tagsArray.length > 0 && tagsArray[0] !== '') {
            return tagsArray;
        }

        return undefined;
    }, [value]);

    const handleChange = (newArray: string[] | undefined) => {
        const newString = newArray?.join(',');

        onChange(newString);
    };

    const validateInput = (tag: string, existingTags: string[]) => {
        if (hasUniqueValues && existingTags.includes(tag)) {
            toastify({text: 'You already used this value', type: 'warning'});

            return false;
        }

        const newString = [tag, ...existingTags].join(',');

        if (rules?.validate && typeof rules.validate === 'function') {
            const validationResult = rules.validate(newString);

            if (validationResult !== true) {
                toastify({text: validationResult as string, type: 'error'});

                return false;
            }
        }

        return true;
    };

    return (
        <span className={inputStyles.root}>
            <span
                className={clsx(
                    inputStyles.inputWrapper,
                    !title && inputStyles.noTitle,
                )}
            >
                {(title || icon) && (
                    <div className={inputStyles.title}>
                        {title && (
                            <span>
                                {title}
                            </span>
                        )}
                        {icon && icon}
                    </div>
                )}
                {values && (
                    <TagsInputComponent
                        value={values}
                        onChange={handleChange}
                        name={name}
                        placeHolder={placeholder}
                        seprators={['Enter', ',']}
                        beforeAddValidate={validateInput}
                        disableBackspace={true}
                        classNames={{
                            input: styles.input,
                            container: styles.container,

                        }}
                    />
                )}
                <span
                    className={clsx(
                        inputStyles.messages,
                        isError && inputStyles.error,
                    )}
                >
                    {error && error.message}
                    {(!error && caption) && caption}
                </span>
            </span>
        </span>
    );
};

export default TagsInput;
